import React, { useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import MuiTable from '@mui/material/Table';
import EDBox from '../EDBox/EDBox';
import { EDStickyTableContextProvider } from './Contexts/EDStickyTableContext';
import { useIsSmallScreen } from '../../Hooks/useFullscreenView';

const SCROLLBAR_MARGIN = 20;

interface IProps {
  width: number;
  height: number;
  colCount: number;
  rowCount: number;
  minColWidth?: number;
  minRowHeight?: number;
  firstColWidth?: number;
  firstRowHeight?: number;
  firstColSticky?: boolean;
  firstRowSticky?: boolean;
  initialScrollToRow?: number;
  // Styles for cells
  cellAlignment?: React.CSSProperties['justifyContent'];
  cellVerticalAlignment?: React.CSSProperties['alignItems'];
  cellTextAlignment?: React.CSSProperties['textAlign'];
  cellBackground?: React.CSSProperties['background'];
  cellDirection?: React.CSSProperties['flexDirection'];
  cellPadding?: React.CSSProperties['padding'];
  cellAllowOverflow?: boolean;
  selectedBackground?: React.CSSProperties['background'];
  todayBackground?: React.CSSProperties['background'];
  outsideBackground?: React.CSSProperties['background'];
  // Override styles for top left cell
  firstCellAlignment?: React.CSSProperties['justifyContent'];
  firstCellVerticalAlignment?: React.CSSProperties['alignItems'];
  firstCellTextAlignment?: React.CSSProperties['textAlign'];
  firstCellBackground?: React.CSSProperties['background'];
  firstCellPadding?: React.CSSProperties['padding'];
  firstCellAllowOverflow?: boolean;
  // Override styles for left column
  firstColAlignment?: React.CSSProperties['justifyContent'];
  firstColVerticalAlignment?: React.CSSProperties['alignItems'];
  firstColTextAlignment?: React.CSSProperties['textAlign'];
  firstColBackground?: React.CSSProperties['background'];
  firstColPadding?: React.CSSProperties['padding'];
  firstColAllowOverflow?: boolean;
  // Override styles for top row
  firstRowAlignment?: React.CSSProperties['justifyContent'];
  firstRowVerticalAlignment?: React.CSSProperties['alignItems'];
  firstRowTextAlignment?: React.CSSProperties['textAlign'];
  firstRowBackground?: React.CSSProperties['background'];
  firstRowDirection?: React.CSSProperties['flexDirection'];
  firstRowPadding?: React.CSSProperties['padding'];
  firstRowAllowOverflow?: boolean;
  /** Extra bottom spacing inside the scroll area to clear a floating menu */
  bottomSpacing?: number | string;
}

/**
 *Tabell där första kolumnen och/eller första raden kan göras sticky, medan övriga kolumner och rader anpassar sig dynamiskt efter givet utrymme, om inte en minsta storlek angivits.
 *
 *#### Struktur:
 *
 *    <EDStickyTable>
 *      <EDStickyTableHeader>              // valfri, används för sticky header
 *        <EDStickyTableHeaderStickyCell>  // valfri, används för sticky första kolumncell i headern
 *        <EDStickyTableHeaderCell>        // för övriga celler i headern
 *      <EDStickyTableRows>
 *        <EDStickyTableRow>               // för varje rad
 *          <EDStickyTableStickyCell>      // valfri, används för sticky första kolumncell i raden
 *          <EDStickyTableCell>            // för övriga celler i raden
 */

export const EDStickyTable: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const isSmallScreen = useIsSmallScreen();

  const firstColWidth = Math.max(props.firstColWidth ?? 0, 0);
  const headerHeight = Math.max(props.firstRowHeight ?? 0, 0);

  const minTableWidth =
    props.colCount * (props.minColWidth ?? 0) +
    (firstColWidth > 0 ? firstColWidth - (props.minColWidth ?? 0) : 0);
  const minTableHeight =
    props.rowCount * (props.minRowHeight ?? 0) +
    (headerHeight > 0 ? headerHeight - (props.minRowHeight ?? 0) : 0);

  const scrollBarHorz = minTableWidth > props.width ? SCROLLBAR_MARGIN : 0;
  const scrollBarVert = minTableHeight > props.height ? SCROLLBAR_MARGIN : 0;

  const responsiveColCount = props.colCount - (firstColWidth > 0 ? 1 : 0);
  const responsiveRowCount = props.rowCount - (headerHeight > 0 ? 1 : 0);

  const responsiveColWidth = Math.max(
    props.minColWidth ?? 0,
    Math.floor(
      (props.width - firstColWidth - scrollBarVert) / responsiveColCount
    )
  );
  const responsiveRowHeight = Math.max(
    props.minRowHeight ?? 0,
    Math.floor(
      (props.height - headerHeight - scrollBarHorz) / responsiveRowCount
    )
  );
  const tableWidth = isSmallScreen
    ? '100%'
    : firstColWidth + responsiveColCount * responsiveColWidth + 'px';
  const tableHeight = headerHeight + responsiveRowCount * responsiveRowHeight;

  const cssVariables = React.useMemo(
    () =>
      ({
        '--container-width': `${props.width}px`,
        '--container-height': `${props.height}px`,
        '--table-width': tableWidth,
        '--table-height': `${tableHeight}px`,
        '--header-height': `${headerHeight}px`,
        '--row-height': `${responsiveRowHeight}px`,
        '--col-width': `${responsiveColWidth}px`,
        '--first-col-width': `${firstColWidth}px`,
        '--header-position': props.firstRowSticky ? 'sticky' : 'relative',
        '--first-col-position': props.firstColSticky ? 'sticky' : 'relative',
        '--overflow-x': scrollBarHorz ? 'auto' : 'hidden',
        '--overflow-y': scrollBarVert ? 'auto' : 'hidden',
        '--cell-alignment': props.cellAlignment,
        '--cell-verticalAlignment': props.cellVerticalAlignment,
        '--cell-textAlignment': props.cellTextAlignment,
        '--cell-background': props.cellBackground,
        '--cell-direction': props.cellDirection,
        '--cell-padding': props.cellPadding,
        '--cell-selectedBackground': props.selectedBackground,
        '--cell-todayBackground': props.todayBackground,
        '--cell-outsideBackground': props.outsideBackground,
        '--cell-overflow': props.cellAllowOverflow ? 'visible' : 'hidden',
        '--first-cell-alignment': props.firstCellAlignment,
        '--first-cell-verticalAlignment': props.firstCellVerticalAlignment,
        '--first-cell-textAlignment': props.firstCellTextAlignment,
        '--first-cell-background': props.firstCellBackground,
        '--first-cell-padding': props.firstCellPadding,
        '--first-cell-overflow': props.firstCellAllowOverflow
          ? 'visible'
          : 'hidden',
        '--first-col-alignment': props.firstColAlignment,
        '--first-col-verticalAlignment': props.firstColVerticalAlignment,
        '--first-col-textAlignment': props.firstColTextAlignment,
        '--first-col-background': props.firstColBackground,
        '--first-col-padding': props.firstColPadding,
        '--first-col-overflow': props.firstColAllowOverflow
          ? 'visible'
          : 'hidden',
        '--first-row-alignment': props.firstRowAlignment,
        '--first-row-verticalAlignment': props.firstRowVerticalAlignment,
        '--first-row-textAlignment': props.firstRowTextAlignment,
        '--first-row-direction': props.firstRowDirection,
        '--first-row-background': props.firstRowBackground,
        '--first-row-padding': props.firstRowPadding,
        '--first-row-overflow': props.firstRowAllowOverflow
          ? 'visible'
          : 'hidden',
      } as React.CSSProperties),
    [
      props.width,
      props.height,
      props.firstRowSticky,
      props.firstColSticky,
      props.cellAlignment,
      props.cellVerticalAlignment,
      props.cellTextAlignment,
      props.cellBackground,
      props.cellDirection,
      props.cellPadding,
      props.selectedBackground,
      props.todayBackground,
      props.outsideBackground,
      props.cellAllowOverflow,
      props.firstCellAlignment,
      props.firstCellVerticalAlignment,
      props.firstCellTextAlignment,
      props.firstCellBackground,
      props.firstCellPadding,
      props.firstCellAllowOverflow,
      props.firstColAlignment,
      props.firstColVerticalAlignment,
      props.firstColTextAlignment,
      props.firstColBackground,
      props.firstColPadding,
      props.firstColAllowOverflow,
      props.firstRowAlignment,
      props.firstRowVerticalAlignment,
      props.firstRowTextAlignment,
      props.firstRowDirection,
      props.firstRowBackground,
      props.firstRowPadding,
      props.firstRowAllowOverflow,
      tableWidth,
      tableHeight,
      headerHeight,
      responsiveRowHeight,
      responsiveColWidth,
      firstColWidth,
      scrollBarHorz,
      scrollBarVert,
    ]
  );

  // Beräknia initial scrollposition
  const scrollBoxRef = useRef(null);
  const initialScrollToRow = props.initialScrollToRow ?? 0;
  useEffect(() => {
    if (scrollBoxRef.current && initialScrollToRow > 0) {
      (scrollBoxRef.current as HTMLElement).scrollTop =
        responsiveRowHeight * initialScrollToRow;
    }
  }, [responsiveRowHeight, initialScrollToRow]);

  return (
    <StyledTableContainer
      style={cssVariables}
      ref={scrollBoxRef}
      data-scroll-container="sticky-table" // Används för att identifiera scrollcontainern i Kalender
    >
      <StyledTable>
        <EDStickyTableContextProvider
          responsiveRowHeight={responsiveRowHeight}
          bottomSpacing={props.bottomSpacing ?? 0}
        >
          {props.children}
        </EDStickyTableContextProvider>
      </StyledTable>
    </StyledTableContainer>
  );
};

const StyledTableContainer = styled(EDBox, { name: 'StickyTableContainer' })(
  ({ theme }) => ({
    position: 'relative',
    boxSizing: 'border-box',
    width: 'var(--container-width)',
    height: 'var(--container-height)',
    overflowX: 'var(--overflow-x)' as React.CSSProperties['overflowX'],
    overflowY: 'var(--overflow-y)' as React.CSSProperties['overflowY'],
    borderTop: `1px solid${theme.customProperties.Drag.background}`,
  })
);

const StyledTable = styled(MuiTable, { name: 'StickyTable' })(({ theme }) => ({
  padding: 0,
  width: 'var(--table-width)',
  height: 'var(--table-height)',
  boxSizing: 'border-box',
  tableLayout: 'fixed',
  position: 'relative',
  backgroundColor: theme.palette.background.paper,
}));
